import { GetOverallAnalyticsResponse } from '@/modules/dashboard/type'

export interface CardMetric {
	label: string
	value: number
	category: 'main' | 'reward'
}

export function extractCardMetrics(
	data: GetOverallAnalyticsResponse,
): CardMetric[] {
	const mainMetrics: CardMetric[] = [
		{
			label: 'ยอดฝากทั้งหมด',
			value: data.totalDepositAmount,
			category: 'main',
		},
		{
			label: 'ยอดถอนทั้งหมด',
			value: data.totalWithdrawAmount,
			category: 'main',
		},
		{
			label: 'กำไร',
			value: data.totalDepositAmount - data.totalWithdrawAmount,
			category: 'main',
		},
		{
			label: 'ยอดฝากไม่รับโปร',
			value: data.totalDepositAmountWithoutPromotion,
			category: 'main',
		},
		{
			label: 'ยอดโบนัสฝากทั้งหมด',
			value: data.totalDepositBonusAmount,
			category: 'main',
		},
		{
			label: 'เครดิตค้างพร้อมถอน',
			value: data.totalMainWalletCredit,
			category: 'main',
		},
		{
			label: 'เครดิตค้างติดเทิร์น',
			value: data.totalPromotionWalletCredit,
			category: 'main',
		},
		{
			label: 'เครติด AFF',
			value: data.totalInviteFriendRewardTransfer,
			category: 'main',
		},
		{
			label: 'เครดิตจากกิจกรรม',
			value: data.totalEventCreditTransfer,
			category: 'main',
		},
		{
			label: 'ผู้ใช้ที่มียอดเดิมพัน',
			value: data.totalPlayCount,
			category: 'main',
		},
		{
			label: 'จำนวนสมัครใหม่',
			value: data.totalNewPlayer,
			category: 'main',
		},
		{
			label: 'ยอดถอนรอดำเนินการ',
			value: data.totalWithdrawPendingAmount,
			category: 'main',
		},
		{
			label: 'ยอดถอนรายการไม่สำเร็จ',
			value: data.totalWithdrawFailedAmount,
			category: 'main',
		},
		{
			label: 'ค่าธรรมเนียมธุรกรรมฝากเงิน',
			value: data.totalDepositFeeAmount,
			category: 'main',
		},
		{
			label: 'ค่าธรรมเนียมธุรกรรมถอนเงิน',
			value: data.totalWithdrawFeeAmount,
			category: 'main',
		},
		{
			label: 'แอดมินปรับยอดเครดิต',
			value: data.totalAdminWalletAdjustment,
			category: 'main',
		},
		{
			label: 'แอดมินเติมเครดิตพาร์ทเนอร์',
			value: data.totalAdminPartnerCredit,
			category: 'main',
		},
	]

	const rewardMetrics: CardMetric[] = [
		{
			label: 'รางวัลวงล้อโชค',
			value: data.totalLuckyWheelBonusReward,
			category: 'reward',
		},
		{
			label: 'รางวัลเช็คอินรายวัน',
			value: data.totalDailyCheckInReward || 0,
			category: 'reward',
		},
		{
			label: 'รางวัลคืนยอดเสีย',
			value: data.totalReturnLossBonusReward,
			category: 'reward',
		},
		{
			label: 'เครดิตโอนจากวงล้อโชค',
			value: data.totalLuckyWheelRewardTransfer,
			category: 'reward',
		},
		{
			label: 'เครดิตโอนจากเช็คอินรายวัน',
			value: data.totalDailyCheckInRewardTransfer,
			category: 'reward',
		},
		{
			label: 'เครดิตโอนจากคืนยอดเสีย',
			value: data.totalReturnLossBonusRewardTransfer,
			category: 'reward',
		},
		{
			label: 'เครดิตโอนจากคอมมิชชั่น',
			value: data.totalCommissionRewardTransfer,
			category: 'reward',
		},
		{
			label: 'เครดิตโอนจากพาร์ทเนอร์',
			value: data.totalPartnerRewardTransfer,
			category: 'reward',
		},
	]

	return [...mainMetrics, ...rewardMetrics]
}

export function exportCardMetricsToCsv(
	data: GetOverallAnalyticsResponse,
	startDate?: string,
	endDate?: string,
): void {
	const metrics = extractCardMetrics(data)

	// Add date range to the CSV
	const dateRangeInfo = {
		วันที่เริ่มต้น: startDate || 'ทั้งหมด',
		วันที่สิ้นสุด: endDate || 'ทั้งหมด',
	}

	// Create CSV content
	const rows = [
		// Date range header
		['ช่วงเวลา', ''],
		[dateRangeInfo['วันที่เริ่มต้น'], dateRangeInfo['วันที่สิ้นสุด']],
		['', ''], // Empty row for separation

		// Main metrics header
		['สรุปภาพรวมหลัก', ''],
		...metrics
			.filter((m) => m.category === 'main')
			.map((metric) => [metric.label, metric.value.toLocaleString()]),

		['', ''], // Empty row for separation

		// Reward metrics header
		['รางวัลและโบนัสจากกิจกรรม', ''],
		...metrics
			.filter((m) => m.category === 'reward')
			.map((metric) => [metric.label, metric.value.toLocaleString()]),
	]

	// Convert to CSV format with BOM for Thai language support
	const BOM = '\uFEFF'
	const csvContent =
		BOM +
		[...rows.map((row) => row.map((cell) => `"${cell}"`).join(','))].join('\n')

	// Create and download the file
	const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
	const link = document.createElement('a')
	const url = URL.createObjectURL(blob)

	const filename = `overall-analytics-cards_${startDate || 'all'}_to_${endDate || 'all'}.csv`

	link.setAttribute('href', url)
	link.setAttribute('download', filename)
	link.style.visibility = 'hidden'

	document.body.appendChild(link)
	link.click()
	document.body.removeChild(link)
}
